/**
 * Structure copied from ./payment-columns.tsx (itself from ./columns.tsx) — the same column shapes
 * (a reference, a linked record, right-aligned money, a moment) over
 * vendor/isomorphic/apps/isomorphic/src/app/shared/invoice/invoice-list/columns.tsx, applied to the
 * receipts Tally posts to the Customers ledger (§17 Finance). A receipt is read-only, so its ⋮ menu
 * only leads elsewhere: the customer it was applied to, and that customer's orders.
 */
import { createColumnHelper } from "@tanstack/react-table";
import { Link } from "react-router-dom";
import { money, moment } from "../../lib/services/finance";
import type { ReceiptRow } from "../../lib/services/customers";
import { formatDate } from "../../lib/format";
import { PiEyeDuotone as EyeIcon, PiShoppingCartDuotone as OrdersIcon } from "react-icons/pi";
import { RowActions } from "../../components/table/row-actions";
import { customerPath } from "../../lib/paths";

const columnHelper = createColumnHelper<ReceiptRow>();

export const receiptColumns = [
  columnHelper.display({
    id: "actions",
    header: "",
    cell: ({ row }) => (
      <RowActions
        name={`receipt ${row.original.voucherNumber ?? "from Tally"}`}
        primary={{ label: "View customer", icon: EyeIcon, to: customerPath(row.original.customerNumber || row.original.customerId) }}
        more={[{ label: "Orders from this customer", icon: OrdersIcon, to: `/orders?customerId=${row.original.customerId}` }]}
      />
    ),
  }),
  columnHelper.accessor((receipt) => receipt.voucherNumber ?? "", {
    id: "voucherNumber",
    header: "Tally voucher",
    size: 200,
    cell: ({ row }) => <span className="font-mono text-sm">{row.original.voucherNumber ?? "—"}</span>,
  }),
  columnHelper.accessor("customerName", {
    header: "Customer",
    size: 240,
    cell: ({ row }) => (
      <Link to={customerPath(row.original.customerNumber || row.original.customerId)} className="text-primary hover:underline">
        {row.original.customerName}
      </Link>
    ),
  }),
  columnHelper.accessor("documentDate", {
    header: "Voucher date",
    size: 150,
    cell: ({ row }) => formatDate(row.original.documentDate),
  }),
  columnHelper.accessor((receipt) => Number(receipt.amount), {
    id: "amount",
    header: "Amount",
    size: 150,
    meta: { align: "right" },
    cell: ({ row }) => <span className="font-semibold tabular-nums text-gray-900">{money(row.original.amount)}</span>,
  }),
  columnHelper.accessor("appliedAt", {
    header: "Applied",
    size: 200,
    cell: ({ row }) => <span className="text-muted-foreground">{moment(row.original.appliedAt)}</span>,
  }),
];
